// web_components/wcContent/wcContent_Utils.ts

import { WcContent } from './wcContent';
import { debounce } from '../../utils/debounce';
import { dispatchAddToBasket } from '../../events/addToBasketEvent';

/**
 * Utility class for handling wc-content components.
 */
export class WcContent_Utils {
	private static instances: Set<WcContent> = new Set();
	private static tallestTextHeight: number = 0;
	private static debouncedResizeHandler: () => void;

	private static readonly MARGIN_ADJUSTMENT = 120;
	private static readonly ASPECT_RATIO_TALL = 3 / 4;
	private static readonly ASPECT_RATIO_LARGE = 4 / 3;

	/**
	 * Initializes the layout adjustment for all wc-content instances.
	 * Should be called on DOMContentLoaded.
	 *
	 * @returns {void}
	 */
	public static initialiseLayoutAdjustments(): void {
		// Debounced resize handler
		this.debouncedResizeHandler = debounce(() => {
			this.measureAndAdjustLayout();
		}, 200);

		window.addEventListener('resize', this.debouncedResizeHandler);
	}

	/**
	 * Registers a wc-content instance for layout management.
	 *
	 * @param {WcContent} instance - The wc-content instance to register.
	 * @returns {void}
	 */
	public static registerInstance(instance: WcContent): void {
		this.instances.add(instance);
		// Delay the measurement to ensure the component is fully rendered
		this.scheduleMeasurement();
	}

	/**
	 * Unregisters a wc-content instance.
	 *
	 * @param {WcContent} instance - The wc-content instance to unregister.
	 * @returns {void}
	 */
	public static unregisterInstance(instance: WcContent): void {
		this.instances.delete(instance);
		this.scheduleMeasurement();
	}

	/**
	 * Schedules a measurement and layout adjustment.
	 * Uses requestIdleCallback if available, otherwise falls back to requestAnimationFrame.
	 *
	 * @returns {void}
	 */
	private static scheduleMeasurement(): void {
		if ('requestIdleCallback' in window) {
			(window as any).requestIdleCallback(() => {
				this.measureAndAdjustLayout();
			});
		} else {
			requestAnimationFrame(() => {
				this.measureAndAdjustLayout();
			});
		}
	}

	/**
	 * Measures the text heights and adjusts layouts for all registered instances.
	 *
	 * @returns {void}
	 */
	private static measureAndAdjustLayout(): void {
		this.tallestTextHeight = 0;

		// Measure all instances
		this.instances.forEach((instance) => {
			const textHeight = this.measureTextHeightWithMargin(instance);
			if (textHeight > this.tallestTextHeight) {
				this.tallestTextHeight = textHeight;
			}
		});

		// Adjust layouts
		this.instances.forEach((instance) => {
			this.adjustLayout(instance, this.tallestTextHeight);
		});
	}

	/**
	 * Measures the height of the text content with added margin for a given wc-content instance.
	 *
	 * @param {WcContent} wcContent - The wc-content instance to measure.
	 * @returns {number} The height of the element with margin.
	 */
	private static measureTextHeightWithMargin(wcContent: WcContent): number {
		const copyWrapper = wcContent.shadowRoot?.querySelector(
			'.wc-content__copy-wrapper',
		) as HTMLElement;
		if (!copyWrapper) {
			return 0;
		}

		// Get the width of the wc-content element
		const wcContentElement = wcContent.shadowRoot?.getElementById(
			wcContent.instanceId,
		) as HTMLElement;
		if (!wcContentElement) {
			return 0;
		}

		// Set the copyWrapper width to half of wc-content's width
		const wcContentWidth = wcContentElement.offsetWidth;
		const copyWrapperWidth = wcContentWidth / 2;

		// Temporarily set the width to measure the height accurately
		const originalWidth = copyWrapper.style.width;
		copyWrapper.style.width = `${copyWrapperWidth}px`;

		// Force reflow
		copyWrapper.getBoundingClientRect();

		const height = copyWrapper.offsetHeight + this.MARGIN_ADJUSTMENT;

		// Reset the width to its original value
		copyWrapper.style.width = originalWidth;

		return height;
	}

	/**
	 * Adjusts the layout of a wc-content instance based on the tallest text block height.
	 *
	 * @param {WcContent} wcContent - The wc-content instance to adjust.
	 * @param {number} tallestTextHeight - The height of the tallest text block from all instances.
	 * @returns {void}
	 */
	private static adjustLayout(wcContent: WcContent, tallestTextHeight: number): void {
		const wcContentElement = wcContent.shadowRoot?.getElementById(
			wcContent.instanceId,
		) as HTMLElement;
		if (!wcContentElement) {
			return;
		}
		const halfWidth = wcContentElement.offsetWidth / 2;

		const imageHeightTall = halfWidth / this.ASPECT_RATIO_TALL;
		const imageHeightLarge = halfWidth / this.ASPECT_RATIO_LARGE;

		const shadowDomMediaWrapper = wcContent.shadowRoot?.querySelector(
			'.wc-content__media-wrapper',
		) as HTMLElement;

		if (!shadowDomMediaWrapper) {
			return;
		}

		// Reset layout classes and order at the beginning
		wcContentElement.classList.remove(
			'wc-content--small',
			'wc-content--medium',
			'wc-content--tall',
			'wc-content--large',
		);
		shadowDomMediaWrapper.style.order = '0';

		let variables = [];

		const jsonLdScript = wcContent.querySelector('script[type="application/ld+json"]');
		if (jsonLdScript) {
			try {
				variables = JSON.parse(<string>jsonLdScript.textContent);
			} catch (e) {
				console.error('wcContent Component: Invalid JSON-LD:', e);
				return;
			}
		}

		const imagePosition = variables.additionalProperty.find(
			(prop: { name: string }) => prop.name === 'Image Position',
		).value;

		const shadowDomImg = wcContent.shadowRoot?.querySelector(
			'.wc-content__image',
		) as HTMLImageElement;

		// Apply layout based on calculated heights and screen width
		if (imageHeightLarge > tallestTextHeight) {
			wcContentElement.classList.add('wc-content--large');
			if (shadowDomImg) shadowDomImg.src = variables.image.representations[3].url;
			if (imagePosition === 'right') {
				shadowDomMediaWrapper.style.order = '2'; // Apply right positioning only for large
			}
		} else if (window.innerWidth > 420 && imageHeightTall > tallestTextHeight) {
			// Apply tall layout only if screen is wide enough and imageHeightTall is greater than text height
			wcContentElement.classList.add('wc-content--tall');
			if (shadowDomImg) shadowDomImg.src = variables.image.representations[2].url;
			if (imagePosition === 'right') {
				shadowDomMediaWrapper.style.order = '2'; // Apply right positioning only for tall
			}
		} else if (window.innerWidth > 420) {
			wcContentElement.classList.add('wc-content--medium');
			if (shadowDomImg) shadowDomImg.src = variables.image.representations[1].url;
		} else {
			wcContentElement.classList.add('wc-content--small');
			if (shadowDomImg) shadowDomImg.src = variables.image.representations[0].url;
		}
	}

	/**
	 * Checks whether the provided string is not empty and ends with a valid image extension.
	 *
	 * @param {string} value - The string to validate.
	 * @returns {boolean} True if the string is valid, false otherwise.
	 */
	public static isValidImage(value: string): boolean {
		const validExtensions = /\.(jpg|jpeg|png|webp)$/i;
		return value !== '' && validExtensions.test(value);
	}
}

/**
 * Handles the form submission by processing the product type, quantity, and selected option.
 *
 * This method is called when the form is submitted. It retrieves the product type,
 * quantity, and selected option from the form, logs them to the console, and allows
 * further actions like sending the data via an AJAX request or updating the UI.
 *
 * @param {HTMLElement} targetElement - The product.
 * @param {string} productId - The id of the product.
 * @param {string} productType - The type of product being added to the cart (e.g., default, Mixed).
 * @param {string} quantity - The quantity of the product to be added to the cart.
 * @param {boolean} isPersonalized - Is this a personalised product?
 * @returns {void} This function does not return anything, but it handles form submission logic.
 */
export function handleFormSubmission(
	targetElement: HTMLElement,
	productId: number,
	productType: string,
	quantity: string,
	isPersonalized: boolean,
): void {
	isPersonalized = false;
	dispatchAddToBasket({
		targetElement,
		productId,
		productType,
		quantity,
		isPersonalized,
	});
}
